// useAdminFeatureFlags — read/write app_settings.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { callRpc } from '@/lib/supabaseTyped';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

export interface FeatureFlag {
  key: string;
  value: unknown;
  updated_at: string;
}

// S-037: app_settings finns i Database-types (src/integrations/supabase/types.ts).
// Tidigare cast med "as unknown as { from: ... }" dolde typningen och tystade
// tsc om kolumner ändrades. Direkt-anropet matchar nu auto-genererade
// Row-shape:n; updated_at kan vara null per Database-definitionen så vi
// normaliserar till '' vid mappning.
async function fetchFlags(): Promise<FeatureFlag[]> {
  const { data } = await supabase
    .from('app_settings')
    .select('key, value, updated_at');
  if (!data) return [];
  return data.map((row) => ({
    key: row.key,
    value: row.value,
    updated_at: row.updated_at ?? '',
  }));
}

export function useAdminFeatureFlags() {
  // app_settings RLS kräver authenticated → gate query på session så
  // anonyma users (eller stale-session-state under sign-in) inte
  // triggar 401-spam i konsolen.
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['admin-flags'],
    queryFn: fetchFlags,
    staleTime: 60_000,
    enabled: isAuthenticated,
  });
}

/**
 * useFeatureFlag — läser en enskild flagga från app_settings. Returnerar
 * `defaultValue` om flaggan inte finns eller queryn inte laddat ännu.
 * Använder samma underliggande query som `useAdminFeatureFlags`, så
 * båda delar cache.
 */
export function useFeatureFlag<T>(key: string, defaultValue: T): T {
  const { data } = useAdminFeatureFlags();
  if (!data) return defaultValue;
  const flag = data.find((f) => f.key === key);
  if (!flag) return defaultValue;
  return (flag.value as T) ?? defaultValue;
}

export function useSetFeatureFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { key: string; value: unknown }) => {
      const { data, error } = await callRpc<{ success: boolean; error?: string }>(
        'admin_set_feature_flag',
        { flag_key: input.key, flag_value: input.value },
      );
      if (error) return { success: false, error: error.message };
      return data ?? { success: false, error: 'no-response' };
    },
    onSuccess: (result, input) => {
      if (!result.success) {
        toast({ title: 'Flag update failed', description: result.error, variant: 'destructive' });
        return;
      }
      toast({ title: 'Flag updated', description: input.key });
      qc.invalidateQueries({ queryKey: ['admin-flags'] });
    },
  });
}
